import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Loader2, DollarSign, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const $ = (cents) => `$${((cents || 0) / 100).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;
const toCents = (d) => Math.round(Number(d || 0) * 100);
const MODELS = ["subscription", "per_application", "per_policy", "hybrid"];
const APP_MS = ["created", "completed", "submitted"];
const POL_MS = ["issued", "placed", "active", "first_premium"];
const PM = ["stripe", "manual", "internal", "hybrid"];

function MoneyField({ label, cents, onChange, testid }) {
  return (
    <div><Label className="text-xs">{label} ($)</Label>
      <Input type="number" value={(cents || 0) / 100} onChange={(e) => onChange(toCents(e.target.value))} data-testid={testid} /></div>
  );
}

export function PlatformBilling({ agencies }) {
  const { t } = useTranslation();
  const [sel, setSel] = useState(agencies[0]?.id || "");
  const [cfg, setCfg] = useState(null);
  const [usage, setUsage] = useState([]);
  const [stripe, setStripe] = useState(null);
  const [templates, setTemplates] = useState({});
  const [report, setReport] = useState(null);
  const [busy, setBusy] = useState(false);

  const loadFor = (id) => {
    if (!id) return;
    api.get(`/billing/config/${id}`).then(({ data }) => setCfg(data.config)).catch(() => {});
    api.get("/billing/usage", { params: { agency_id: id } }).then(({ data }) => setUsage(data)).catch(() => {});
    api.get("/billing/stripe/status", { params: { agency_id: id } }).then(({ data }) => setStripe(data)).catch(() => {});
  };
  useEffect(() => { api.get("/billing/templates").then(({ data }) => setTemplates(data)).catch(() => {}); api.get("/reports/platform").then(({ data }) => setReport(data)).catch(() => {}); }, []);
  useEffect(() => { loadFor(sel); }, [sel]);

  const set = (k, v) => setCfg((p) => ({ ...p, [k]: v }));
  const save = async () => {
    setBusy(true);
    try { const { data } = await api.put(`/billing/config/${sel}`, cfg); setCfg(data.config); toast.success(t("billing.saved")); }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || t("billing.failed")); }
    finally { setBusy(false); }
  };
  const applyTpl = async (tpl) => { const { data } = await api.post(`/billing/config/${sel}/apply-template`, { template: tpl }); setCfg(data.config); toast.success(t("billing.applied", { tpl })); };
  const review = async (id, action) => { try { await api.post(`/billing/usage/${id}/review`, { action }); toast.success(t(`billing.${action}`)); loadFor(sel); } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); } };
  const checkout = async (kind) => {
    try { const { data } = await api.post(`/billing/stripe/checkout/${sel}`, { kind }); if (data.checkout_url) { navigator.clipboard?.writeText(data.checkout_url); toast.success(t("billing.checkout_copied")); } }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || t("billing.checkout_required")); }
  };

  return (
    <div className="space-y-4" data-testid="platform-billing">
      {report && (
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4" data-testid="platform-billing-report">
          <Kpi label={t("billing.report.verified_mrr")} value={$(report.verified_mrr_cents)} />
          <Kpi label={t("billing.report.manual_recurring")} value={$(report.manual_contracted_recurring_cents)} sub={t("billing.report.not_verified")} />
          <Kpi label={t("billing.report.pending_usage")} value={$(report.pending_usage_cents)} />
          <Kpi label={t("billing.report.approved_usage")} value={$(report.approved_usage_cents)} />
        </div>
      )}
      <div className="flex items-center gap-2">
        <Label className="text-xs">{t("billing.agency")}</Label>
        <Select value={sel} onValueChange={setSel}>
          <SelectTrigger className="w-64" data-testid="billing-agency-select"><SelectValue placeholder={t("billing.select_agency")} /></SelectTrigger>
          <SelectContent>{agencies.map((a) => <SelectItem key={a.id} value={a.id}>{a.display_name || a.name}</SelectItem>)}</SelectContent>
        </Select>
        <div className="ml-auto flex gap-1">
          {Object.entries(templates).map(([k, v]) => (
            <Button key={k} size="sm" variant="outline" onClick={() => applyTpl(k)} data-testid={`billing-tpl-${k}`}>{v.label}</Button>
          ))}
        </div>
      </div>

      {cfg && (
        <div className="grid gap-4 lg:grid-cols-2">
          <div className="space-y-3 rounded-lg border bg-card p-4 text-sm" data-testid="billing-config-form">
            <div className="font-semibold">{t("billing.config_title")}</div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">{t("billing.model")}</Label>
                <Select value={cfg.model} onValueChange={(v) => set("model", v)}>
                  <SelectTrigger data-testid="billing-model"><SelectValue /></SelectTrigger>
                  <SelectContent>{MODELS.map((m) => <SelectItem key={m} value={m}>{t(`billing.models.${m}`)}</SelectItem>)}</SelectContent>
                </Select></div>
              <div><Label className="text-xs">{t("billing.payment_method")}</Label>
                <Select value={cfg.payment_method} onValueChange={(v) => set("payment_method", v)}>
                  <SelectTrigger data-testid="billing-payment-method"><SelectValue /></SelectTrigger>
                  <SelectContent>{PM.map((m) => <SelectItem key={m} value={m}>{t(`billing.pm.${m}`)}</SelectItem>)}</SelectContent>
                </Select></div>
              <MoneyField label={t("billing.impl_fee")} cents={cfg.implementation_fee_cents} onChange={(c) => set("implementation_fee_cents", c)} testid="billing-impl-fee" />
              <MoneyField label={t("billing.monthly_fee")} cents={cfg.monthly_base_fee_cents} onChange={(c) => set("monthly_base_fee_cents", c)} testid="billing-monthly-fee" />
              <div><Label className="text-xs">{t("billing.included_apps")}</Label><Input type="number" value={cfg.included_applications} onChange={(e) => set("included_applications", Number(e.target.value))} data-testid="billing-incl-apps" /></div>
              <MoneyField label={t("billing.per_app_fee")} cents={cfg.per_application_fee_cents} onChange={(c) => set("per_application_fee_cents", c)} testid="billing-per-app" />
              <div><Label className="text-xs">{t("billing.app_milestone")}</Label>
                <Select value={cfg.application_milestone} onValueChange={(v) => set("application_milestone", v)}>
                  <SelectTrigger data-testid="billing-app-ms"><SelectValue /></SelectTrigger>
                  <SelectContent>{APP_MS.map((m) => <SelectItem key={m} value={m}>{t(`billing.app_ms.${m}`)}</SelectItem>)}</SelectContent>
                </Select></div>
              <div><Label className="text-xs">{t("billing.included_pol")}</Label><Input type="number" value={cfg.included_policies} onChange={(e) => set("included_policies", Number(e.target.value))} data-testid="billing-incl-pol" /></div>
              <MoneyField label={t("billing.per_pol_fee")} cents={cfg.per_policy_fee_cents} onChange={(c) => set("per_policy_fee_cents", c)} testid="billing-per-pol" />
              <div><Label className="text-xs">{t("billing.pol_milestone")}</Label>
                <Select value={cfg.policy_milestone} onValueChange={(v) => set("policy_milestone", v)}>
                  <SelectTrigger data-testid="billing-pol-ms"><SelectValue /></SelectTrigger>
                  <SelectContent>{POL_MS.map((m) => <SelectItem key={m} value={m}>{t(`billing.pol_ms.${m}`)}</SelectItem>)}</SelectContent>
                </Select></div>
              <div><Label className="text-xs">{t("billing.rev_share")}</Label><Input type="number" value={cfg.revenue_share_pct} onChange={(e) => set("revenue_share_pct", Number(e.target.value))} data-testid="billing-rev-share" /></div>
              <div><Label className="text-xs">{t("billing.user_limit")}</Label><Input type="number" value={cfg.user_limit} onChange={(e) => set("user_limit", Number(e.target.value))} data-testid="billing-user-limit" /></div>
              <MoneyField label={t("billing.min_commit")} cents={cfg.min_monthly_commitment_cents} onChange={(c) => set("min_monthly_commitment_cents", c)} testid="billing-min-commit" />
              <div><Label className="text-xs">{t("billing.contract_ref")}</Label><Input value={cfg.contract_reference || ""} onChange={(e) => set("contract_reference", e.target.value)} data-testid="billing-contract-ref" /></div>
            </div>
            <div className="flex items-center gap-3">
              <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={cfg.active} onChange={(e) => set("active", e.target.checked)} data-testid="billing-active" /> {t("billing.active")}</label>
              <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={cfg.manual_review_required} onChange={(e) => set("manual_review_required", e.target.checked)} data-testid="billing-manual-review" /> {t("billing.manual_review")}</label>
              <Button size="sm" onClick={save} disabled={busy} className="ml-auto" data-testid="billing-save">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : t("billing.save")}</Button>
            </div>
          </div>

          <div className="space-y-4">
            <div className="rounded-lg border bg-card p-4 text-sm" data-testid="billing-stripe">
              <div className="mb-2 font-semibold">{t("billing.stripe")}</div>
              {stripe && (stripe.configured
                ? <div className="space-y-2"><div className="text-emerald-600">{t("billing.configured")}</div>
                    <div className="flex gap-2"><Button size="sm" variant="outline" onClick={() => checkout("implementation")} data-testid="billing-checkout-impl">{t("billing.setup_checkout")}</Button>
                      <Button size="sm" variant="outline" onClick={() => checkout("subscription")} data-testid="billing-checkout-sub">{t("billing.sub_checkout")}</Button></div></div>
                : <div className="flex items-center gap-2 text-amber-600" data-testid="billing-stripe-notconfigured"><AlertTriangle className="h-4 w-4" /> {t("billing.not_configured", { missing: (stripe.missing || []).join(", ") })}</div>)}
            </div>
            <div className="rounded-lg border bg-card p-4 text-sm" data-testid="billing-usage">
              <div className="mb-2 font-semibold">{t("billing.usage_ledger")}</div>
              {usage.length === 0 ? <div className="text-muted-foreground">{t("billing.no_usage")}</div> : (
                <div className="max-h-72 space-y-1 overflow-y-auto">
                  {usage.map((u) => (
                    <div key={u.id} className="flex items-center justify-between rounded border px-2 py-1.5 text-xs" data-testid={`usage-${u.id}`}>
                      <div><span className="font-medium">{$(u.amount_cents)}</span> · {u.source_type}/{u.milestone} · <span className="text-amber-600">{u.status}</span></div>
                      {["pending", "pending_review"].includes(u.status) && (
                        <div className="flex gap-1">
                          <button onClick={() => review(u.id, "approve")} data-testid={`usage-approve-${u.id}`} className="text-emerald-600">{t("billing.approve")}</button>
                          <button onClick={() => review(u.id, "waive")} data-testid={`usage-waive-${u.id}`} className="text-slate-500">{t("billing.waive")}</button>
                          <button onClick={() => review(u.id, "reverse")} data-testid={`usage-reverse-${u.id}`} className="text-red-600">{t("billing.reverse")}</button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const Kpi = ({ label, value, sub }) => (
  <div className="rounded-lg border bg-card p-3"><div className="text-xs text-muted-foreground">{label}</div>
    <div className="text-lg font-semibold tabular-nums">{value}</div>{sub ? <div className="text-[10px] text-muted-foreground">{sub}</div> : null}</div>
);

export function AgencyBilling() {
  const { t } = useTranslation();
  const [s, setS] = useState(null);
  const [inv, setInv] = useState([]);
  useEffect(() => { api.get("/billing/summary").then(({ data }) => setS(data)).catch(() => {}); api.get("/billing/invoices").then(({ data }) => setInv(data)).catch(() => {}); }, []);
  const portal = async () => { try { const { data } = await api.post(`/billing/stripe/portal/${s.agency_id}`); if (data.portal_url) window.open(data.portal_url, "_blank"); } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || t("billing.portal_unavailable")); } };
  if (!s) return <Loader2 className="h-4 w-4 animate-spin" />;
  const modelLabel = s.model ? (t(`billing.models.${s.model}`, { defaultValue: s.model })) : "—";
  const pmLabel = s.payment_method ? (t(`billing.pm.${s.payment_method}`, { defaultValue: s.payment_method })) : "—";
  return (
    <div className="max-w-2xl space-y-4 rounded-lg border bg-card p-4 text-sm" data-testid="agency-billing">
      <div className="flex items-center gap-2 font-semibold"><DollarSign className="h-4 w-4" /> {t("billing.overview")}</div>
      <div className="grid grid-cols-2 gap-3">
        <Row k={t("billing.row_model")} v={modelLabel} />
        <Row k={t("billing.row_plan")} v={s.plan_template} />
        <Row k={t("billing.row_payment")} v={pmLabel} />
        <Row k={t("billing.row_monthly")} v={$(s.monthly_base_fee_cents)} />
        <Row k={t("billing.row_incl_apps")} v={s.included_applications} />
        <Row k={t("billing.row_incl_pol")} v={s.included_policies} />
        <Row k={t("billing.row_user_limit")} v={s.user_limit || "—"} />
        <Row k={t("billing.row_subscription")} v={s.subscription_status || (s.stripe_configured ? t("billing.sub_none") : t("billing.sub_not_configured"))} />
      </div>
      {!s.stripe_configured && <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800" data-testid="agency-billing-notice">{t("billing.agency_notice")}</div>}
      <div>
        <div className="mb-1 text-xs font-semibold text-muted-foreground">{t("billing.invoices")}</div>
        {inv.length === 0 ? <div className="text-xs text-muted-foreground">{t("billing.no_invoices")}</div> :
          inv.map((i) => <div key={i._id} className="flex justify-between text-xs"><span>{i._id}</span><span>{$(i.amount_cents)} · {i.status}</span></div>)}
      </div>
      {s.stripe_configured && <Button size="sm" variant="outline" onClick={portal} data-testid="agency-billing-portal">{t("billing.portal")}</Button>}
    </div>
  );
}

const Row = ({ k, v }) => <div className="flex justify-between border-b py-1"><span className="text-muted-foreground">{k}</span><span className="font-medium">{v}</span></div>;
